import { UnitSystem } from '../UnitSystem';

// Parcel interface
export interface Parcel {
  id: string;
  quantity: number;
  weight: number;
  length: number;
  width: number;
  height: number;
  physicalWeight: number;
  dimensionalWeight: number;
  price: number;
  currency: string;
}

// Helper function to create an empty parcel
export const createEmptyParcel = (
  weight?: number, 
  dimensions?: { length: number; width: number; height: number }
): Parcel => ({
  id: `parcel-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
  quantity: 1,
  weight: weight || 1,
  length: dimensions?.length || 10,
  width: dimensions?.width || 10,
  height: dimensions?.height || 10,
  physicalWeight: weight || 1,
  dimensionalWeight: 0,
  price: 0,
  currency: 'ILS'
});

// Dimensional weight calculation
export const calculateDimensionalWeight = (
  length: number,
  width: number,
  height: number,
  unitSystem: UnitSystem
): number => {
  if (length <= 0 || width <= 0 || height <= 0) {
    return 0;
  }

  // Volume in cubic units
  const volume = length * width * height;

  // Apply dimensional factor based on unit system
  // Standard divisors: 5,000 for metric (cm->kg), 139 for imperial (in->lb)
  const divisor = unitSystem === 'metric' ? 5000 : 139;
  
  return parseFloat((volume / divisor).toFixed(2));
};

// Calculate physical weight (weight * quantity)
export const calculatePhysicalWeight = (
  weight: number,
  quantity: number
): number => {
  if (weight <= 0 || quantity <= 0) {
    return 0;
  }
  
  return parseFloat((weight * quantity).toFixed(2));
};

// Calculate chargeable weight (max of physical and dimensional weight)
export const calculateChargeableWeight = (
  physicalWeight: number,
  dimensionalWeight: number
): number => {
  return Math.max(physicalWeight, dimensionalWeight);
};

// Convert weight between unit systems
export const convertWeight = (
  weight: number,
  fromUnitSystem: UnitSystem,
  toUnitSystem: UnitSystem
): number => {
  if (fromUnitSystem === toUnitSystem) {
    return weight;
  }
  
  // Convert kg to lb (1 kg = 2.20462 lb)
  if (fromUnitSystem === 'metric' && toUnitSystem === 'imperial') {
    return parseFloat((weight * 2.20462).toFixed(2));
  }
  
  // Convert lb to kg (1 lb = 0.453592 kg)
  return parseFloat((weight * 0.453592).toFixed(2));
};

// Convert dimensions between unit systems
export const convertDimension = (
  dimension: number,
  fromUnitSystem: UnitSystem,
  toUnitSystem: UnitSystem
): number => {
  if (fromUnitSystem === toUnitSystem) {
    return dimension;
  }
  
  // Convert cm to in (1 cm = 0.393701 in)
  if (fromUnitSystem === 'metric' && toUnitSystem === 'imperial') {
    return parseFloat((dimension * 0.393701).toFixed(2));
  }
  
  // Convert in to cm (1 in = 2.54 cm)
  return parseFloat((dimension * 2.54).toFixed(2));
};

// Convert a parcel's measurements between unit systems
export const convertParcelUnits = (
  parcel: Parcel,
  fromUnitSystem: UnitSystem,
  toUnitSystem: UnitSystem
): Parcel => {
  if (fromUnitSystem === toUnitSystem) {
    return parcel;
  }
  
  const convertedParcel: Parcel = {
    ...parcel,
    weight: convertWeight(parcel.weight, fromUnitSystem, toUnitSystem),
    length: convertDimension(parcel.length, fromUnitSystem, toUnitSystem),
    width: convertDimension(parcel.width, fromUnitSystem, toUnitSystem),
    height: convertDimension(parcel.height, fromUnitSystem, toUnitSystem),
    physicalWeight: convertWeight(parcel.physicalWeight, fromUnitSystem, toUnitSystem),
    dimensionalWeight: convertWeight(parcel.dimensionalWeight, fromUnitSystem, toUnitSystem)
  };
  
  return convertedParcel;
};

// Calculate total weight of all parcels
export const calculateTotalWeight = (parcels: Parcel[]): number => {
  if (parcels.length === 0) {
    return 0;
  }
  
  return parcels.reduce((total, parcel) => {
    const chargeableWeight = calculateChargeableWeight(
      parcel.physicalWeight,
      parcel.dimensionalWeight
    );
    return total + chargeableWeight;
  }, 0);
};

// Update a parcel's calculated weights
export const updateParcelWeights = (
  parcel: Parcel,
  unitSystem: UnitSystem
): Parcel => {
  const physicalWeight = calculatePhysicalWeight(parcel.weight, parcel.quantity);
  const dimensionalWeight = calculateDimensionalWeight(
    parcel.length,
    parcel.width,
    parcel.height,
    unitSystem
  );
  
  return {
    ...parcel,
    physicalWeight,
    dimensionalWeight
  };
}; 